import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useAcceptTeamInvite } from "@/hooks/useShelterTeam";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, AlertCircle, KeyRound, Users, XCircle } from "lucide-react";
import { AuthHeroIcon } from "@/components/auth/AuthHeroIcon";

const AcceptTeamInvite = () => {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = params.get("token") ?? "";

  const accept = useAcceptTeamInvite();
  const [state, setState] = useState<"idle" | "running" | "ok" | "err">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [shelterName, setShelterName] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdDone, setPwdDone] = useState(false);
  const [pwdError, setPwdError] = useState<{ title: string; hint: string } | null>(null);

  const pwdChecks = {
    length: password.length >= 10,
    lower: /[a-ząćęłńóśźż]/.test(password),
    upper: /[A-ZĄĆĘŁŃÓŚŹŻ]/.test(password),
    digit: /\d/.test(password),
    special: /[^A-Za-z0-9ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(password),
  };
  const pwdScore = Object.values(pwdChecks).filter(Boolean).length;
  const pwdStrong = pwdChecks.length && pwdChecks.lower && pwdChecks.upper && pwdChecks.digit;
  const pwdMatches = password.length > 0 && password === password2;
  const strengthLabel =
    pwdScore <= 2 ? "Słabe" : pwdScore === 3 ? "Średnie" : pwdScore === 4 ? "Dobre" : "Mocne";
  const strengthColor =
    pwdScore <= 2 ? "bg-destructive" : pwdScore === 3 ? "bg-orange-500" : pwdScore === 4 ? "bg-yellow-500" : "bg-primary";

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const next = `/panel/zespol/dolacz?token=${encodeURIComponent(token)}`;
      navigate(`/logowanie?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    if (!token) {
      setState("err");
      setErrorMsg("Brak tokena zaproszenia w linku");
      return;
    }
    if (state !== "idle") return;
    setState("running");
    accept
      .mutateAsync(token)
      .then((res) => {
        setShelterName(res.shelter?.name ?? null);
        setState("ok");
        qc.invalidateQueries({ queryKey: ["owned-shelters", user.id] });
        qc.invalidateQueries({ queryKey: ["shelter-team"] });
      })
      .catch((e: any) => {
        setErrorMsg(e?.message ?? "Nie udało się przyjąć zaproszenia");
        setState("err");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, token]);

  const translateAuthError = (err: any): { title: string; hint: string } => {
    const raw = (err?.message || err?.error_description || "").toString();
    const code = (err?.code || "").toString();
    const lower = raw.toLowerCase();

    if (code === "same_password" || lower.includes("should be different")) {
      return {
        title: "To hasło jest takie samo jak poprzednie.",
        hint: "Wpisz inne hasło, którego nie używałaś/eś wcześniej na tym koncie.",
      };
    }
    if (code === "weak_password" || lower.includes("password") && lower.includes("weak")) {
      return {
        title: "Hasło jest zbyt słabe według naszego dostawcy.",
        hint: "Spróbuj dłuższego hasła z mieszanką wielkich i małych liter, cyfr oraz znaku specjalnego.",
      };
    }
    if (lower.includes("at least") && lower.includes("character")) {
      return {
        title: "Hasło jest za krótkie.",
        hint: "Użyj minimum 10 znaków - im dłuższe, tym lepsze.",
      };
    }
    if (code === "over_request_rate_limit" || lower.includes("rate limit") || lower.includes("too many")) {
      return {
        title: "Za dużo prób w krótkim czasie.",
        hint: "Odczekaj minutę i spróbuj ponownie.",
      };
    }
    if (lower.includes("session") && (lower.includes("missing") || lower.includes("expired") || lower.includes("invalid"))) {
      return {
        title: "Twoja sesja wygasła.",
        hint: "Otwórz link z zaproszenia ponownie z maila, aby zalogować się świeżo.",
      };
    }
    if (lower.includes("network") || lower.includes("failed to fetch") || lower.includes("load failed")) {
      return {
        title: "Brak połączenia z serwerem.",
        hint: "Sprawdź internet i spróbuj jeszcze raz za chwilę.",
      };
    }
    return {
      title: raw || "Nie udało się ustawić hasła.",
      hint: "Spróbuj ponownie. Jeśli problem się powtarza, napisz na kontakt@gdzieschronisko.pl",
    };
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    if (!pwdStrong) {
      const err = {
        title: "Hasło nie spełnia wymagań.",
        hint: "Uzupełnij brakujące elementy z listy poniżej (długość, wielkie/małe litery, cyfra).",
      };
      setPwdError(err);
      toast.error(err.title);
      return;
    }
    if (password !== password2) {
      const err = {
        title: "Hasła nie są identyczne.",
        hint: "Wpisz to samo hasło w obu polach.",
      };
      setPwdError(err);
      toast.error(err.title);
      return;
    }
    setSavingPwd(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      setSavingPwd(false);
      if (error) {
        const translated = translateAuthError(error);
        setPwdError(translated);
        toast.error(translated.title);
        return;
      }
      setPwdDone(true);
      toast.success("Hasło zostało ustawione");
    } catch (e: any) {
      setSavingPwd(false);
      const translated = translateAuthError(e);
      setPwdError(translated);
      toast.error(translated.title);
    }
  };

  // Czy użytkownik loguje się przez Google (ma identity 'google')?
  const hasGoogleIdentity = !!user?.identities?.some((i) => i.provider === "google");

  return (
    <>
      <Helmet>
        <title>Zaproszenie do zespołu | Panel</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-xl mx-auto">
        <Card>
          <CardHeader className="p-4 sm:p-6 pb-2">
            <CardTitle className="text-base sm:text-lg">Zaproszenie do zespołu schroniska</CardTitle>
            <CardDescription>
              Po przyjęciu zaproszenia otrzymasz dostęp do edycji danych schroniska.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-2 space-y-4">
            {state === "running" && (
              <div className="flex flex-col items-center text-center gap-4 py-6">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 blur-xl" />
                  <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                    <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Przyjmujemy zaproszenie...</p>
              </div>
            )}
            {state === "ok" && (
              <div className="space-y-5 py-2">
                <div className="flex flex-col items-center text-center gap-4 pt-2">
                  <AuthHeroIcon icon={Users} tone="primary" sparkle />
                  <div className="space-y-1">
                    <p className="text-lg font-bold">Witamy w zespole!</p>
                    {shelterName && (
                      <p className="text-sm text-muted-foreground max-w-sm">
                        Dołączyłaś/eś do zespołu schroniska <strong className="text-foreground">{shelterName}</strong>.
                      </p>
                    )}
                  </div>
                </div>


                {!pwdDone && !hasGoogleIdentity && (
                  <form onSubmit={handleSetPassword} className="space-y-3 rounded-md border bg-muted/30 p-3 sm:p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <KeyRound className="h-4 w-4" />
                      Ustaw hasło do logowania
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Aby móc logować się w przyszłości bez linku z maila, ustaw teraz hasło do swojego konta.
                    </p>
                    <div className="space-y-1">
                      <Label htmlFor="pwd">Nowe hasło</Label>
                      <Input
                        id="pwd"
                        type="password"
                        autoComplete="new-password"
                        minLength={10}
                        required
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setPwdError(null); }}
                        placeholder="min. 10 znaków"
                      />
                      {password.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                              <div
                                className={`h-full transition-all ${strengthColor}`}
                                style={{ width: `${(pwdScore / 5) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground w-14 text-right">
                              {strengthLabel}
                            </span>
                          </div>
                          <ul className="text-[11px] text-muted-foreground space-y-0.5">
                            <li className={pwdChecks.length ? "text-primary" : ""}>
                              {pwdChecks.length ? "✓" : "•"} min. 10 znaków
                            </li>
                            <li className={pwdChecks.lower && pwdChecks.upper ? "text-primary" : ""}>
                              {pwdChecks.lower && pwdChecks.upper ? "✓" : "•"} małe i wielkie litery
                            </li>
                            <li className={pwdChecks.digit ? "text-primary" : ""}>
                              {pwdChecks.digit ? "✓" : "•"} co najmniej jedna cyfra
                            </li>
                            <li className={pwdChecks.special ? "text-primary" : ""}>
                              {pwdChecks.special ? "✓" : "•"} znak specjalny (zalecane)
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="pwd2">Powtórz hasło</Label>
                      <Input
                        id="pwd2"
                        type="password"
                        autoComplete="new-password"
                        minLength={10}
                        required
                        value={password2}
                        onChange={(e) => { setPassword2(e.target.value); setPwdError(null); }}
                      />
                      {password2.length > 0 && !pwdMatches && (
                        <p className="text-[11px] text-destructive">Hasła nie są identyczne</p>
                      )}
                    </div>
                    {pwdError && (
                      <div
                        role="alert"
                        className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
                      >
                        <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                        <div className="space-y-0.5 min-w-0">
                          <p className="text-sm font-medium text-destructive">{pwdError.title}</p>
                          <p className="text-xs text-muted-foreground">{pwdError.hint}</p>
                        </div>
                      </div>
                    )}
                    <Button
                      type="submit"
                      disabled={savingPwd || !pwdStrong || !pwdMatches}
                      className="w-full sm:w-auto"
                    >
                      {savingPwd && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Ustaw hasło i przejdź do panelu
                    </Button>
                  </form>
                )}

                {(pwdDone || hasGoogleIdentity) && (
                  <div className="flex justify-center">
                    <Button asChild size="lg" className="gap-2">
                      <Link to="/panel">Przejdź do panelu →</Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
            {state === "err" && (
              <div className="flex flex-col items-center text-center gap-5 py-4">
                <AuthHeroIcon icon={XCircle} tone="destructive" />
                <div className="space-y-1">
                  <p className="text-base font-semibold">Nie udało się przyjąć zaproszenia</p>
                  <p className="text-sm text-muted-foreground max-w-sm">{errorMsg}</p>
                </div>
                <Button asChild variant="outline" size="lg">
                  <Link to="/panel">Wróć do panelu</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AcceptTeamInvite;
